import { useState } from 'react';
import {
  CoverageBadge,
  Icon,
  Panel,
  Screen,
  ScreenHeader,
  StatTile,
  SummaryTiles,
  Workspace,
  WorkspacePane,
} from '@garnet/ui';
import { cellOutcome, deriveGate, splitCellKey } from '../data/coverage.js';
import type { StudioContext } from '../studio.js';

function GateMatrix({
  ctx,
  coveredMin,
  thinMin,
  selected,
  onSelect,
}: {
  ctx: StudioContext;
  coveredMin: number;
  thinMin: number;
  selected: string;
  onSelect: (key: string) => void;
}) {
  const { api } = ctx;
  const domains = api.domains();
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${domains.length}, minmax(96px, 1fr))`, minWidth: 860 }}>
        <div style={{ borderBottom: '1px solid var(--border)' }} />
        {domains.map((d) => (
          <div
            key={d.key}
            style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)', borderLeft: '1px solid var(--border-subtle)', fontSize: 10.5, fontWeight: 600, color: 'var(--fg-2)' }}
          >
            {d.short}
          </div>
        ))}
        {api.jurisdictions().map((j) => (
          <div key={j.code} style={{ display: 'contents' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '0 10px',
                borderBottom: '1px solid var(--border-subtle)',
                borderRight: '1px solid var(--border)',
              }}
            >
              <span className="mono" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--garnet-text)' }}>
                {j.code}
              </span>
            </div>
            {domains.map((d) => {
              const key = `${j.code}:${d.key}`;
              const c = api.coverageCell(key);
              if (!c) return <div key={key} />;
              const gate = deriveGate(c.rules, coveredMin, thinMin);
              const flipped = c.gate === 'answer' && gate !== 'answer';
              const bg = gate === 'answer' ? 'var(--cov-covered-bg)' : gate === 'abstain' ? 'var(--cov-thin-bg)' : 'var(--cov-empty-bg)';
              return (
                <div
                  key={key}
                  onClick={() => onSelect(key)}
                  role="button"
                  aria-pressed={selected === key}
                  style={{
                    minHeight: 46,
                    padding: '7px 8px',
                    borderLeft: '1px solid var(--border-subtle)',
                    borderBottom: '1px solid var(--border-subtle)',
                    background: bg,
                    cursor: 'pointer',
                    position: 'relative',
                    outline: selected === key ? '2px solid var(--accent)' : flipped ? '1px solid var(--status-warn)' : 'none',
                    outlineOffset: -2,
                  }}
                >
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color: gate === 'answer' ? 'var(--status-pass-text)' : gate === 'abstain' ? 'var(--abstain-text)' : 'var(--fg-3)',
                      letterSpacing: '0.03em',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    {gate === 'no-cov' ? 'not covered' : gate}
                    {flipped && (
                      <span
                        className="dot"
                        style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--status-warn-text)', animation: 'dotpulse 1.4s infinite' }}
                      />
                    )}
                  </div>
                  <div className="mono" style={{ fontSize: 13, color: c.state === 'empty' ? 'var(--fg-3)' : 'var(--fg-1)', marginTop: 3 }}>
                    {c.state === 'empty' ? '—' : c.rules}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function EdgeSimulator({ ctx, cellKey }: { ctx: StudioContext; cellKey: string }) {
  const { api } = ctx;
  const { juris, domain } = splitCellKey(cellKey);
  const c = api.coverageCell(cellKey);
  const dom = api.domains().find((d) => d.key === domain);
  if (!c || !dom) return null;
  const which = cellOutcome(c.state);
  const cards = {
    answer: {
      head: 'Answers',
      accent: 'var(--status-pass)',
      big: 'Class IIb',
      body: 'Cited to Regulation (EU) 2017/745 — Annex VIII, Rule 12.',
      foot: 'from release 2026.06.1',
    },
    abstain: {
      head: 'Abstains',
      accent: 'var(--abstain-text)',
      big: 'No answer',
      body: 'No rule covers this question. The Advisor declines and the engine returns nothing — it never guesses.',
      foot: 'a designed state, not an error',
    },
    nocov: {
      head: 'Not covered',
      accent: 'var(--fg-3)',
      big: 'Nothing held',
      body: 'Nothing is held here yet. Every question returns “not covered” until a rule is written.',
      foot: 'this cell is on the backlog',
    },
  } as const;
  const r = cards[which];
  return (
    <div>
      <div className="metatext" style={{ marginBottom: 10 }}>
        At{' '}
        <span className="mono" style={{ color: 'var(--garnet)' }}>
          {juris} · {dom.short}
        </span>
        , a question to Garnet Core:
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            background: 'var(--bg-sunken)',
          }}
        >
          <span className="dot" style={{ width: 7, height: 7, borderRadius: '50%', background: r.accent }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: r.accent }}>{r.head}</span>
        </div>
        <div style={{ padding: '14px 12px' }}>
          <div className="serif" style={{ fontSize: 22, color: which === 'answer' ? 'var(--garnet)' : 'var(--fg-2)', marginBottom: 6 }}>
            {r.big}
          </div>
          <p className="metatext" style={{ lineHeight: 1.55 }}>
            {r.body}
          </p>
          <div className="metatext mono" style={{ marginTop: 8, color: 'var(--fg-3)' }}>
            {r.foot}
          </div>
        </div>
      </div>
      <div className="metatext" style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <Icon name="shield" size={12} color="var(--abstain-text)" style={{ marginTop: 1 }} />
        Beyond the gate, the Advisor abstains and the engine returns nothing. Neither guesses a result.
      </div>
    </div>
  );
}

export function Coverage({ ctx }: { ctx: StudioContext }) {
  const { api } = ctx;
  const [coveredMin, setCoveredMin] = useState(40);
  const [thinMin, setThinMin] = useState(1);
  const [sel, setSel] = useState('EU:medical');
  const gateEditable = ctx.can('gate');

  const cells = api.coverageList();
  const flips = cells.filter((c) => c.gate === 'answer' && c.rules < coveredMin).length;
  const answering = cells.filter((c) => c.rules >= coveredMin).length;
  const abstaining = cells.filter((c) => c.rules < coveredMin && c.rules >= thinMin).length;
  const noCover = cells.filter((c) => c.rules < thinMin).length;

  const backlog = Object.entries(api.coverage())
    .filter(([, c]) => c.state !== 'covered')
    .sort((a, b) => b[1].noCoverage - a[1].noCoverage)
    .slice(0, 7);

  return (
    <Screen workspace>
      <ScreenHeader
        eyebrow="§ Coverage & abstention"
        title="Serving gate"
        lead="Where Garnet Core answers and where it abstains. The gate is editable; abstention is a designed state, not an error."
        actions={
          <SummaryTiles min={120} style={{ minWidth: 360 }}>
            <StatTile label="Answering" value={answering} valueStyle={{ color: 'var(--status-pass-text)' }} />
            <StatTile label="Abstaining" value={abstaining} valueStyle={{ color: 'var(--status-warn-text)' }} />
            <StatTile label="No-coverage" value={noCover} />
          </SummaryTiles>
        }
      />

      <Workspace style={{ gridTemplateColumns: '1fr 340px' }}>
        <WorkspacePane flush>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)', minWidth: 0 }}>
          <Panel
            flush
            title="Gate map"
            right={
              flips > 0 ? (
                <span className="badge b-warn">
                  <span className="dot pulse" style={{ background: 'var(--status-warn-text)' }} />
                  {flips} cell{flips === 1 ? '' : 's'} would drop to abstain
                </span>
              ) : (
                <span className="metatext mono">no flips at current threshold</span>
              )
            }
          >
            <div style={{ padding: 'var(--pad-x)' }}>
              <GateMatrix ctx={ctx} coveredMin={coveredMin} thinMin={thinMin} selected={sel} onSelect={setSel} />
            </div>
          </Panel>

          <Panel
            title="Gate thresholds"
            right={
              gateEditable ? (
                <span className="label" style={{ color: 'var(--garnet)' }}>
                  editable
                </span>
              ) : (
                <span className="badge b-neutral">
                  <Icon name="eye" size={11} />
                  admin only
                </span>
              )
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: gateEditable ? 1 : 0.6 }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                  <span className="field-label" style={{ margin: 0 }}>
                    Covered →{' '}
                    <span className="mono" style={{ color: 'var(--status-pass-text)' }}>
                      answer
                    </span>{' '}
                    · min rules
                  </span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--fg-1)' }}>
                    {coveredMin}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={200}
                  value={coveredMin}
                  disabled={!gateEditable}
                  onChange={(e) => setCoveredMin(+e.target.value)}
                  style={{ width: '100%', accentColor: 'var(--garnet)' }}
                />
                <div className="metatext" style={{ marginTop: 4 }}>
                  Below this, a cell abstains instead of answering.
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7 }}>
                  <span className="field-label" style={{ margin: 0 }}>
                    Thin →{' '}
                    <span className="mono" style={{ color: 'var(--abstain-text)' }}>
                      abstain
                    </span>{' '}
                    floor · min rules
                  </span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--fg-1)' }}>
                    {thinMin}
                  </span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={30}
                  value={thinMin}
                  disabled={!gateEditable}
                  onChange={(e) => setThinMin(+e.target.value)}
                  style={{ width: '100%', accentColor: 'var(--garnet)' }}
                />
                <div className="metatext" style={{ marginTop: 4 }}>
                  Below this, a cell returns “not covered” — it holds nothing.
                </div>
              </div>
              <div className="metatext" style={{ display: 'flex', alignItems: 'flex-start', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
                <Icon name={gateEditable ? 'alert' : 'shield'} size={12} color="var(--status-warn-text)" style={{ marginTop: 1 }} />
                {gateEditable
                  ? 'Raising the bar tightens what the Advisor answers — shown here before it ships.'
                  : 'Only an admin can move the gate. Switch role to Admin to edit.'}
              </div>
            </div>
          </Panel>
          </div>
        </WorkspacePane>

        <WorkspacePane flush>
          <Panel title="Edge of coverage" right={<span className="metatext mono">live</span>}>
            <EdgeSimulator ctx={ctx} cellKey={sel} />
          </Panel>
          <Panel flush title="Gap backlog" right={<span className="metatext mono">by no-coverage rate</span>} style={{ marginTop: 'var(--gap)' }}>
            <div>
              {backlog.map(([k, c]) => {
                const { juris, domain } = splitCellKey(k);
                const dom = api.domains().find((d) => d.key === domain);
                return (
                  <div
                    key={k}
                    onClick={() => ctx.openStore(k)}
                    role="button"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px var(--pad-x)',
                      borderBottom: '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span className="mono" style={{ fontSize: 11, color: 'var(--garnet-text)', width: 24 }}>
                        {juris}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{dom?.short}</span>
                      <CoverageBadge state={c.state} />
                    </div>
                    <span className="mono" style={{ fontSize: 12, color: c.noCoverage > 50 ? 'var(--status-fail-text)' : 'var(--status-warn-text)' }}>
                      {c.noCoverage}%
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>
        </WorkspacePane>
      </Workspace>
    </Screen>
  );
}
